import styled from "@emotion/styled";
import {
  type CommunicationFormValues,
  type Opportunity,
  sendCommunication,
} from "@repo/api";
import {
  FormField,
  Input,
  LightMode,
  Option,
  Select,
  Text,
  Variables,
} from "@repo/skapa";
import { getLanguageAndLocaleFromUrl, t } from "@repo/translation";
import {
  ModalSheet,
  SectionContainer,
  TwoColumnSectionContainer,
  translateLanguage,
  useCustomer,
  useSettings,
  useToast,
} from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { EmailAttachments } from "./sendCommunication/EmailAttachments";
import { EmailEditor } from "./sendCommunication/EmailEditor";
import { EmailMultiInput } from "./sendCommunication/EmailMultiInput";
import { useTemplatesByHfb } from "./sendCommunication/useTemplatesByHfb";
import { useCommunicationDraft } from "./useCommunicationDraft";
import { useTemplateContent } from "./useTemplateContent";
import { useUserEditTracker } from "./useUserEditTracker";

const EmailContainer = styled.div`
  background-color: ${LightMode.colourStaticLightGrey};
  padding: ${Variables.space100} ${Variables.space100} ${Variables.space300};
  border-radius: ${Variables.radiusS};
  border: 1px solid ${LightMode.colourNeutral4};
`;

const EmailEditorWrapper = styled.div`
  margin-top: ${Variables.space150};
`;

const EMPTY_FORM_VALUES: CommunicationFormValues = {
  selectedLanguage: "en",
  selectedTemplateKey: "",
  toRecipients: [],
  ccRecipients: [],
  subject: "",
  body: "",
  attachments: [],
};

interface Props {
  isOpen: boolean;
  opportunity: Opportunity;
  onClose: () => void;
  onBack?: () => void;
}

export const CommunicationModal = ({
  isOpen,
  opportunity,
  onClose,
  onBack,
}: Props) => {
  const { updateToast } = useToast();
  const { general } = useSettings();
  const urlLanguage = getLanguageAndLocaleFromUrl()?.language ?? "en";
  const supportedLanguages = useMemo(
    () =>
      Array.from(
        new Set([...(general?.supported_languages ?? []), urlLanguage])
      ),
    [general?.supported_languages, urlLanguage]
  );
  const emptyValues = useMemo<CommunicationFormValues>(
    () => ({ ...EMPTY_FORM_VALUES, selectedLanguage: urlLanguage }),
    [urlLanguage]
  );
  const { customer } = useCustomer(opportunity.id);
  const customerEmail = customer?.email;

  const templates = useTemplatesByHfb(opportunity);
  const enabledTemplates = useMemo(
    () => templates.filter((t) => t.status),
    [templates]
  );

  const form = useForm<CommunicationFormValues>({
    defaultValues: emptyValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid, isSubmitting },
  } = form;

  const selectedTemplateKey = useWatch({
    control,
    name: "selectedTemplateKey",
  });
  const selectedLanguage = useWatch({ control, name: "selectedLanguage" });

  const { isUserEditedRef, markEdited, resetEdited } = useUserEditTracker();

  const { isDraftRestoredRef, clearDraft } = useCommunicationDraft({
    opportunityId: opportunity.id,
    form,
    isOpen,
    emptyValues,
  });

  useEffect(() => {
    if (
      customerEmail &&
      isOpen &&
      !isDraftRestoredRef.current &&
      form.getValues("toRecipients").length === 0
    ) {
      setValue("toRecipients", [customerEmail], { shouldValidate: true });
    }
  }, [customerEmail, isOpen, isDraftRestoredRef, setValue, form]);

  useTemplateContent({
    selectedTemplateKey,
    selectedLanguage,
    defaultLanguage: general?.default_language_code || "en",
    enabledTemplates,
    setValue,
    isDraftRestoredRef,
    isUserEditedRef,
  });

  const onSend = handleSubmit(async (data) => {
    try {
      trackAnalytics("send_communication");

      await sendCommunication(opportunity.id, {
        to: data.toRecipients,
        cc: data.ccRecipients,
        subject: data.subject,
        bodyHtml: data.body,
        attachmentIds: data.attachments.map((a) => a.id),
      });

      clearDraft();

      updateToast({
        title: t("prompt.communication_sent_success"),
      });
    } catch (err) {
      updateToast({
        title: t("prompt.communication_send_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      onClose();
    }
  });

  const handleResetForm = () => {
    clearDraft();
    resetEdited();

    updateToast({
      title: t("prompt.draft_deleted"),
    });
  };

  return (
    <ModalSheet
      title={t("label.send_communication")}
      visible={isOpen}
      onClose={onClose}
      onBack={onBack}
      size="large"
      primaryButtonProps={{
        text: t("action.send_email"),
        onClick: onSend,
        disabled: !isValid || isSubmitting,
        loading: isSubmitting,
      }}
      secondaryButtonProps={{
        text: t("action.reset_form"),
        onClick: handleResetForm,
      }}
    >
      <SectionContainer>
        <TwoColumnSectionContainer>
          <FormField>
            <Controller
              name="selectedLanguage"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  id="language-selector"
                  label={t("label.language")}
                  onChange={(e) => {
                    resetEdited();
                    isDraftRestoredRef.current = false;
                    field.onChange(e.target.value);
                  }}
                >
                  {supportedLanguages.map((lang) => (
                    <Option
                      key={lang}
                      value={lang}
                      name={translateLanguage(lang)}
                    />
                  ))}
                </Select>
              )}
            />
          </FormField>

          <FormField>
            <Controller
              name="selectedTemplateKey"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  id="template-selector"
                  label={t("label.template")}
                  onChange={(e) => {
                    resetEdited();
                    isDraftRestoredRef.current = false;
                    field.onChange(e.target.value);
                  }}
                >
                  {enabledTemplates.map((template) => (
                    <Option
                      key={template.template_id_key}
                      value={template.template_name_key}
                      name={template.template_name_key}
                    />
                  ))}
                </Select>
              )}
            />
          </FormField>
        </TwoColumnSectionContainer>

        <EmailContainer>
          <Controller
            name="toRecipients"
            control={control}
            rules={{ validate: (v) => (v?.length ?? 0) > 0 }}
            render={({ field, fieldState }) => (
              <FormField
                shouldValidate={fieldState.isDirty && Boolean(fieldState.error)}
                valid={!fieldState.error}
                validation={{
                  msg: t("label.required_field"),
                  id: "to-recipients-error",
                  type: "error",
                }}
              >
                <EmailMultiInput
                  label={t("label.to")}
                  emails={field.value}
                  onChange={field.onChange}
                />
              </FormField>
            )}
          />

          <Controller
            name="ccRecipients"
            control={control}
            render={({ field }) => (
              <EmailMultiInput
                label={t("label.cc")}
                emails={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            name="subject"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FormField
                shouldValidate={fieldState.isDirty && Boolean(fieldState.error)}
                valid={!fieldState.error}
                validation={{
                  msg: t("label.required_field"),
                  id: "subject-error",
                  type: "error",
                }}
              >
                <Input
                  {...field}
                  id="email-subject-input"
                  label={t("label.subject")}
                  type="text"
                  onChange={(e) => {
                    markEdited();
                    field.onChange(e.target.value);
                  }}
                />
              </FormField>
            )}
          />

          <EmailEditorWrapper>
            <Text textStyle="Body.M">{t("label.message")}</Text>
            <Controller
              name="body"
              control={control}
              render={({ field }) => (
                <EmailEditor
                  value={field.value}
                  onChange={(value) => {
                    markEdited();
                    field.onChange(value);
                  }}
                />
              )}
            />
          </EmailEditorWrapper>

          <EmailEditorWrapper>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <EmailAttachments
                  opportunityId={opportunity.id}
                  attachments={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </EmailEditorWrapper>
        </EmailContainer>
      </SectionContainer>
    </ModalSheet>
  );
};
